import React from 'react'
import type { Block, Variable } from '../../editor/types'

interface TabsBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

interface Tab {
  label: string
  value: string
  content: string
}

function interpolateText(
  text: string,
  variableValues: Record<string, number | string | boolean>,
  accent: string
) {
  return text.split(/(@\w+)/g).map((part, i) => {
    const match = part.match(/^@(\w+)$/)
    if (match) {
      const varName = match[1]
      const val = variableValues[varName]
      if (val !== undefined) {
        return (
          <span
            key={i}
            className="font-semibold"
            style={{ color: accent }}
          >
            {String(val)}
          </span>
        )
      }
    }
    return <span key={i}>{part}</span>
  })
}

export default function TabsBlock({ block, variableValues, variables, onSetVariableValue }: TabsBlockProps) {
  const varName = block.variable || ''
  const variable = variables.find((v) => v.name === varName)
  const currentValue = String(variableValues[varName] ?? variable?.default ?? '')
  const config = (block.config || {}) as Record<string, unknown>
  const tabs = (config.tabs as Tab[]) || []
  const accent = block.accent || 'var(--accent, #5048e5)'

  const activeTab = tabs.find((t) => t.value === currentValue) || tabs[0]

  return (
    <div className="space-y-0">
      {/* Tab headers */}
      <div className="flex border-b" style={{ borderColor: 'color-mix(in srgb, var(--block-text, #0f172a) 15%, transparent)' }}>
        {tabs.map((tab) => {
          const isActive = tab.value === (activeTab?.value ?? '')
          return (
            <button
              key={tab.value}
              onClick={() => onSetVariableValue(varName, tab.value)}
              className="px-4 py-2.5 text-sm font-medium transition-all relative"
              style={{
                color: isActive ? 'var(--block-text, #0f172a)' : 'var(--block-text, #94a3b8)',
                opacity: isActive ? 1 : 0.5,
              }}
            >
              {tab.label}
              {isActive && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full"
                  style={{ backgroundColor: accent }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Tab content — only render if tab has inline content */}
      {activeTab?.content && (
        <div className="p-4 text-sm leading-relaxed" style={{ color: 'var(--block-text, #475569)' }}>
          {interpolateText(activeTab.content, variableValues, accent)}
        </div>
      )}
    </div>
  )
}
